import type { SearchSchemaInput } from "@tanstack/react-router";
import type { OrderDto } from "@carry/contract";
import { localToday } from "../lib/format";

export type OrderListStatus = "all" | OrderDto["status"];

export interface OrderListSearch {
  q: string;
  page: number;
  pageSize: number;
  status: OrderListStatus;
  delivery_date: string;
  sort: string;
}

export type OrderListSearchInput = Partial<OrderListSearch> & SearchSchemaInput;

const ORDER_LIST_STATUSES = new Set<OrderListStatus>([
  "all",
  "draft",
  "confirmed",
  "assigned",
  "completed",
  "cancelled",
]);

function positiveInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  if (year == null || month == null || day == null) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * 受注一覧の表示状態を URL search として正規化する。
 *
 * SearchSchemaInput により Link / navigate では全項目を省略可能にしつつ、
 * Route.useSearch() では既定値を補完した OrderListSearch を返す。
 */
export function validateOrderListSearch(search: OrderListSearchInput): OrderListSearch {
  const raw = search as Record<string, unknown>;
  const status =
    typeof raw.status === "string" && ORDER_LIST_STATUSES.has(raw.status as OrderListStatus)
      ? (raw.status as OrderListStatus)
      : "all";

  return {
    q: typeof raw.q === "string" ? raw.q : "",
    page: positiveInteger(raw.page, 1),
    pageSize: positiveInteger(raw.pageSize, 20),
    status,
    delivery_date: isIsoDate(raw.delivery_date) ? raw.delivery_date : localToday(),
    sort: typeof raw.sort === "string" ? raw.sort : "",
  };
}
